import { Injectable } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { Observable, Subject } from 'rxjs';
import { DocumentationService } from './documentation.service';
import { PdfAnalysisOptions } from '../models/pdf-analysis-options.model';
import {
  PdfAnalysisResult,
  PdfAnalysisStatus,
  PdfBlockReasonCode,
  PDF_ANALYSIS_DECISION_MAP,
  PDF_ANALYSIS_DEFAULT_REASONS,
  resolvePdfBlockReasonCode
} from '../models/pdf-analysis-result.model';

export interface PdfAnalysisState {
  analyzing: boolean;
  progress: number;
  status: PdfAnalysisStatus;
  reason?: string;
  // Código interno para resolver el copy del modal de bloqueo (ver PdfInspectorErrorModal).
  // Solo se completa cuando status='block'.
  reasonCode?: PdfBlockReasonCode;
  // true cuando el bloqueo fue producto de un error de red/infra (catch de la request) y no de
  // una decisión de seguridad del backend; en ese caso debe seguir mostrándose el toast, no el modal.
  isNetworkError?: boolean;
  // Presente únicamente cuando status='allow'; debe reenviarse al backend en el alta del
  // documento (ver ECM-13897) para evitar una re-inspección redundante del mismo archivo.
  inspectionToken?: string;
}

@Injectable({ providedIn: 'root' })
export class PdfAnalyzerService {
  constructor (
    private documentationService: DocumentationService,
    private readonly transloco: TranslocoService
  ) { }

  analyze(file: File, options?: Partial<PdfAnalysisOptions>): Observable<PdfAnalysisState> {
    const state$ = new Subject<PdfAnalysisState>();
    let progress = 0;

    const timer = setInterval(() => {
      if (progress < 100) {
        progress += 2;
        state$.next({ analyzing: true, progress, status: null });
      }
    }, 80);

    state$.next({ analyzing: true, progress: 0, status: null });

    this.documentationService.analizeDocument([file], options).toPromise()
      .then((response: PdfAnalysisResult) => {
        clearInterval(timer);
        const status = PDF_ANALYSIS_DECISION_MAP[response?.decision] ?? 'block';
        state$.next({
          analyzing: false,
          progress: 100,
          status,
          reason: this.transloco.translate(PDF_ANALYSIS_DEFAULT_REASONS[status]),
          reasonCode: status === 'block' ? resolvePdfBlockReasonCode(response?.detail) : undefined,
          inspectionToken: response?.inspectionToken
        });
        state$.complete();
      })
      .catch(err => {
        clearInterval(timer);
        // The mapped error carries no message, and the body's is Web API's generic English text: never shown (ECM-15942).
        const reason = err?.error?.reason
          || this.transloco.translate('pdfInspector.pdfAnalyzerService.connectionErrorMsg');
        state$.next({ analyzing: false, progress: 100, status: 'block', reason, isNetworkError: true });
        state$.complete();
      });

    return state$.asObservable();
  }
}
